import type {
  MetaCampaignReportRow,
  MetaPlatform,
  MetaReportGroupBy,
} from "../types";

/**
 * Almikia Epic EM (TASK-068): pure helpers of the "Origen Meta" card and the campaign report
 * screen. No advisor is ever named, counted or ranked here: a row is a campaign, an ad set or an ad.
 */

export const REPORT_GROUPINGS: MetaReportGroupBy[] = [
  "campaign",
  "adset",
  "ad",
];
export const REPORT_PLATFORMS: MetaPlatform[] = ["facebook", "instagram"];
export const DEFAULT_REPORT_DAYS = 30;
export const CTWA_CLID_MAX_LENGTH = 255;
/** Shown in place of any figure the report cannot compute. */
export const EMPTY_CELL = "-";

const CTWA_CLID_PATTERN = /^[A-Za-z0-9_-]+$/;
const MEXICO_TIME_ZONE = "America/Mexico_City";

/** i18n key of a grouping toggle option. */
export const groupingLabelKey = (groupBy: MetaReportGroupBy): string =>
  `crm.metaOrigin.report.group.${groupBy}`;

/** Mexico City calendar day (`YYYY-MM-DD`) of an instant. */
const mexicoDay = (date: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: MEXICO_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

/** Default report range: the last 30 Mexico City days, today included. */
export const defaultReportRange = (
  now: Date = new Date(),
): { from: string; to: string } => ({
  from: mexicoDay(new Date(now.getTime() - (DEFAULT_REPORT_DAYS - 1) * 864e5)),
  to: mexicoDay(now),
});

/** A range is usable when both days are set and `from` is not after `to`. */
export const isValidReportRange = (from: string, to: string): boolean =>
  Boolean(from) && Boolean(to) && from <= to;

/** Conversion rate (0..1) as a percentage with one decimal, or a dash when unavailable. */
export const formatRate = (rate: number | null | undefined): string =>
  rate == null || Number.isNaN(rate)
    ? EMPTY_CELL
    : `${(Math.round(rate * 1000) / 10).toLocaleString("es-MX", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} %`;

/** Median time to first response: minutes under an hour, hours and minutes after; else a dash. */
export const formatFirstResponse = (seconds: number | null | undefined) => {
  if (seconds == null || Number.isNaN(seconds)) return EMPTY_CELL;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest === 0
    ? `${Math.floor(minutes / 60)} h`
    : `${Math.floor(minutes / 60)} h ${rest} min`;
};

/** Sale value as whole pesos. */
export const formatSaleValue = (value: number | null | undefined): string =>
  value == null
    ? EMPTY_CELL
    : new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      }).format(value);

/** Name of the row at the chosen level, falling back to the Meta id, then to a dash. */
export const rowLabel = (
  row: MetaCampaignReportRow,
  groupBy: MetaReportGroupBy,
): string => {
  const [name, id] =
    groupBy === "campaign"
      ? [row.campaign_name, row.campaign_id]
      : groupBy === "adset"
        ? [row.adset_name, row.adset_id]
        : [row.ad_name, row.ad_id];
  return name || id || EMPTY_CELL;
};

/** Keeps the rows of one platform (an empty `platform` keeps every row). */
export const filterByPlatform = (
  rows: MetaCampaignReportRow[],
  platform: MetaPlatform | "",
): MetaCampaignReportRow[] =>
  platform ? rows.filter((row) => row.platform === platform) : rows;

/** Result of validating a manually entered `ctwa_clid`. */
export type CtwaClidValidation =
  | { valid: true; value: string }
  | { valid: false; errorKey: string };

/** Trims the input and rejects empty, oversized or oddly shaped click ids. */
export const validateCtwaClid = (raw: string): CtwaClidValidation => {
  const value = raw.trim();
  if (!value) {
    return { valid: false, errorKey: "crm.metaOrigin.ctwa_required" };
  }
  if (value.length > CTWA_CLID_MAX_LENGTH) {
    return { valid: false, errorKey: "crm.metaOrigin.ctwa_too_long" };
  }
  if (!CTWA_CLID_PATTERN.test(value)) {
    return { valid: false, errorKey: "crm.metaOrigin.ctwa_invalid" };
  }
  return { valid: true, value };
};

/** Re-keys the export rows by their translated header, preserving column order. */
export const withTranslatedHeaders = (
  rows: Record<string, unknown>[],
  translate: (key: string) => string,
): Record<string, unknown>[] =>
  rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        translate(`crm.metaOrigin.report.headers.${key}`),
        value,
      ]),
    ),
  );
